import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const ACTION_COLORS = {
    create: '#22c55e',
    status_change: '#3b82f6',
    assign: '#8b5cf6',
    update: '#9ca3af',
    archive: '#ef4444',
    unarchive: '#22c55e',
    review: '#6b7280',
};

function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0][0].toUpperCase();
}

function relativeTime(isoString) {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    if (diffSec < 60) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    if (diffDay < 30) return `${Math.floor(diffDay / 7)}w ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatAbsoluteDate(isoString) {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true
    });
}

const AuditLogSection = ({ entityType, entityId }) => {
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(true);
    const [skip, setSkip] = useState(0);
    const limit = 50;

    const fetchLogs = useCallback(async (currentSkip = 0, append = false) => {
        if (!entityId) return;
        setLoading(true);
        try {
            const response = await axios.get(`/api/audit-log/${entityType}/${entityId}`, {
                params: { skip: currentSkip, limit }
            });
            if (response.data.success) {
                if (append) {
                    setLogs(prev => [...prev, ...response.data.data]);
                } else {
                    setLogs(response.data.data);
                }
                setTotal(response.data.total);
            }
        } catch (error) {
            console.error('Error fetching audit log:', error);
        } finally {
            setLoading(false);
        }
    }, [entityType, entityId]);

    useEffect(() => {
        setSkip(0);
        fetchLogs(0, false);
    }, [fetchLogs]);

    const handleLoadMore = () => {
        const newSkip = skip + limit;
        setSkip(newSkip);
        fetchLogs(newSkip, true);
    };

    if (!entityId) return null;

    return (
        <div style={{ margin: '0 24px 24px 24px' }}>
            <div
                style={{
                    background: 'white',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    overflow: 'hidden'
                }}
            >
                {/* Header */}
                <div
                    onClick={() => setExpanded(!expanded)}
                    style={{
                        padding: '16px 24px',
                        borderBottom: expanded ? '1px solid #e5e7eb' : 'none',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        userSelect: 'none'
                    }}
                >
                    <h5 style={{ fontWeight: '600', margin: 0, fontSize: '16px' }}>
                        Activity Log
                        {total > 0 && (
                            <span style={{
                                backgroundColor: '#e5e7eb',
                                color: '#374151',
                                fontSize: '12px',
                                padding: '2px 8px',
                                borderRadius: '10px',
                                marginLeft: '8px',
                                fontWeight: '500'
                            }}>
                                {total}
                            </span>
                        )}
                    </h5>
                    <i className={`ti-angle-${expanded ? 'up' : 'down'}`} style={{ color: '#6b7280' }}></i>
                </div>

                {/* Content */}
                {expanded && (
                    <div style={{ padding: '16px 24px' }}>
                        {loading && logs.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>
                                <div className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                <span style={{ marginLeft: '8px' }}>Loading activity...</span>
                            </div>
                        ) : logs.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '24px', color: '#9ca3af' }}>
                                No activity recorded yet.
                            </div>
                        ) : (
                            <>
                                {logs.map((log, index) => {
                                    const borderColor = ACTION_COLORS[log.action] || '#9ca3af';
                                    return (
                                        <div
                                            key={log.id || index}
                                            style={{
                                                display: 'flex',
                                                gap: '12px',
                                                padding: '12px 0',
                                                borderLeft: `3px solid ${borderColor}`,
                                                paddingLeft: '16px',
                                                marginBottom: index < logs.length - 1 ? '4px' : 0
                                            }}
                                        >
                                            {/* Avatar */}
                                            <div style={{
                                                width: '32px',
                                                height: '32px',
                                                borderRadius: '50%',
                                                backgroundColor: '#e5e7eb',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                color: '#374151',
                                                flexShrink: 0
                                            }}>
                                                {getInitials(log.user_name)}
                                            </div>

                                            {/* Content */}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                                                    <div>
                                                        <span style={{ fontWeight: '600', fontSize: '13px', color: '#111827' }}>
                                                            {log.user_name}
                                                        </span>
                                                        <span style={{ color: '#6b7280', fontSize: '13px', marginLeft: '6px' }}>
                                                            {log.description}
                                                        </span>
                                                    </div>
                                                    <span
                                                        title={formatAbsoluteDate(log.created_at)}
                                                        style={{
                                                            color: '#9ca3af',
                                                            fontSize: '12px',
                                                            whiteSpace: 'nowrap',
                                                            flexShrink: 0
                                                        }}
                                                    >
                                                        {relativeTime(log.created_at)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Load More */}
                                {logs.length < total && (
                                    <div style={{ textAlign: 'center', paddingTop: '12px' }}>
                                        <button
                                            onClick={handleLoadMore}
                                            disabled={loading}
                                            className="btn btn-outline-secondary btn-sm"
                                            style={{ fontSize: '13px' }}
                                        >
                                            {loading ? 'Loading...' : `Load more (${total - logs.length} remaining)`}
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AuditLogSection;
